import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminRole, B2bContract, ContractFormat } from "../api/types.js";
import { MutationForm, fieldError } from "../forms/mutation-form.js";
import { bpToSun, count, fromUtcInput, sunAmount, utc } from "../format.js";
import {
  Blank,
  Button,
  ErrorNote,
  Field,
  inputClass,
  Note,
  Panel,
  Pill,
  ScreenHeader,
  ShortId,
  Spinner,
  Table,
  Td,
  Th,
} from "../ui.js";

/**
 * B2B contracts (b2b-pricing.md §2). One per account, optional; no contract is retail.
 *
 * THE FORM IS FORMAT-SPECIFIC, because the three formats are three different products and the
 * API enforces that with three refinements:
 *   `reserve`               needs `reserve_amount` AND `reserve_addresses`, takes no overflow cap;
 *   `on_demand`             takes neither — it refuses `5007` when own capacity is short;
 *   `on_demand_guaranteed`  takes the overflow cap and `offer_ladder_beyond_cap`, and is the only
 *                           format that buys from a provider.
 * A field that does not belong to the chosen format is ABSENT rather than disabled: a disabled
 * reserve-amount box on an on-demand contract invites the question "why can't I fill this in".
 */

const FORMAT_HELP: Record<ContractFormat, string> = {
  reserve:
    "A standing delegation of N energy to named addresses for a term, billed used or not. Capacity is pinned in advance, so there is no overflow.",
  on_demand:
    "Each order separately at the contract price, with NO guarantee: when own capacity is short the order is refused with 5007 and the charge is refunded.",
  on_demand_guaranteed:
    "Each order separately and we must deliver: short on own capacity, we buy from the cheapest provider up to the daily overflow cap.",
};

export function ContractsScreen({ role }: { role: AdminRole }) {
  const [status, setStatus] = useState("");
  const [accountId, setAccountId] = useState("");
  const [creating, setCreating] = useState(false);
  const [closing, setClosing] = useState<B2bContract | null>(null);

  const contracts = useResource(
    (signal) => {
      void signal;
      return adminReads.contracts({
        ...(status === "" ? {} : { status }),
        ...(accountId === "" ? {} : { accountId }),
        limit: 100,
      });
    },
    [status, accountId],
  );

  return (
    <>
      <ScreenHeader
        title="Contracts"
        lead="One contract per account, or none — an account with no contract is priced at the public grid and behaves as on_demand_guaranteed."
        action={
          role === "editor" && !creating ? (
            <Button onClick={() => setCreating(true)}>Create a contract</Button>
          ) : undefined
        }
      />

      {role === "editor" && creating ? (
        <div class="mb-3">
          <Panel>
            <CreateContractForm
              onDone={() => {
                setCreating(false);
                contracts.reload();
              }}
              onCancel={() => setCreating(false)}
            />
          </Panel>
        </div>
      ) : null}

      {closing === null ? null : (
        <div class="mb-3">
          <Panel>
            <CloseContractForm
              contract={closing}
              onDone={() => {
                setClosing(null);
                contracts.reload();
              }}
              onCancel={() => setClosing(null)}
            />
          </Panel>
        </div>
      )}

      <Panel
        title="Contracts"
        action={
          <div class="flex gap-2">
            <select
              class={`${inputClass} w-auto`}
              aria-label="Status"
              value={status}
              onChange={(event) => setStatus((event.currentTarget as HTMLSelectElement).value)}
            >
              <option value="">every status</option>
              <option value="active">active</option>
              <option value="suspended">suspended</option>
              <option value="closed">closed</option>
            </select>
            <input
              class={`${inputClass} w-44`}
              aria-label="Filter by account id"
              placeholder="account id"
              value={accountId}
              onInput={(event) => setAccountId((event.currentTarget as HTMLInputElement).value.trim())}
            />
          </div>
        }
      >
        {contracts.loading ? (
          <Spinner />
        ) : contracts.error !== null ? (
          <ErrorNote error={contracts.error} onRetry={() => contracts.reload()} />
        ) : (
          <ContractTable
            rows={contracts.data?.data ?? []}
            role={role}
            onClose={(contract) => setClosing(contract)}
          />
        )}
      </Panel>
    </>
  );
}

function ContractTable({
  rows,
  role,
  onClose,
}: {
  rows: B2bContract[];
  role: AdminRole;
  onClose(contract: B2bContract): void;
}) {
  return (
    <Table
      rows={rows.length}
      empty="No contract matches. Every account without one is priced at the public grid."
      head={
        <tr>
          <Th>Contract</Th>
          <Th>Account</Th>
          <Th>Format</Th>
          <Th numeric>Price</Th>
          <Th numeric>Reserve</Th>
          <Th numeric>Overflow cap</Th>
          <Th>Beyond cap</Th>
          <Th>Supply policy</Th>
          <Th>Term</Th>
          <Th>Status</Th>
          <Th>Reason</Th>
          {role === "editor" ? <Th>{""}</Th> : null}
        </tr>
      }
    >
      {rows.map((contract) => (
        <tr key={contract.id}>
          <Td mono>
            <ShortId value={contract.id} />
          </Td>
          <Td mono>
            <ShortId value={contract.account_id} />
          </Td>
          <Td>{contract.format}</Td>
          <Td numeric>
            <span title={`${contract.price_bp} bp of one SUN; floor_applies = ${contract.floor_applies}`}>
              {bpToSun(contract.price_bp)}
            </span>
          </Td>
          <Td numeric>
            {contract.reserve_amount === null ? (
              <Blank title="Not a reserve" />
            ) : (
              <span title={contract.reserve_addresses.join("\n")}>
                {count(contract.reserve_amount)} → {contract.reserve_addresses.length} addr
              </span>
            )}
          </Td>
          <Td numeric>
            {contract.overflow_daily_cap_sun === null ? (
              <Blank title="No cap on this format" />
            ) : (
              sunAmount(contract.overflow_daily_cap_sun)
            )}
          </Td>
          <Td>
            {contract.format === "on_demand_guaranteed"
              ? contract.offer_ladder_beyond_cap
                ? "ladder price"
                : "refuse"
              : "—"}
          </Td>
          <Td>{contract.supply_policy}</Td>
          <Td>
            {utc(contract.term_from)} → {contract.term_to === null ? "open" : utc(contract.term_to)}
          </Td>
          <Td>
            <Pill value={contract.status} />
          </Td>
          <Td>{contract.close_reason ?? contract.reason ?? <Blank />}</Td>
          {role === "editor" ? (
            <Td>
              {contract.status === "closed" ? null : (
                <Button tone="ghost" onClick={() => onClose(contract)}>
                  Close
                </Button>
              )}
            </Td>
          ) : null}
        </tr>
      ))}
    </Table>
  );
}

export function CreateContractForm({
  onDone,
  onCancel,
}: {
  onDone(): void;
  onCancel(): void;
}) {
  const [accountId, setAccountId] = useState("");
  const [format, setFormat] = useState<ContractFormat>("on_demand_guaranteed");
  const [priceBp, setPriceBp] = useState("");
  const [reserveAmount, setReserveAmount] = useState("");
  const [reserveAddresses, setReserveAddresses] = useState("");
  const [termFrom, setTermFrom] = useState("");
  const [termTo, setTermTo] = useState("");
  const [overflowCap, setOverflowCap] = useState("");
  const [offerLadder, setOfferLadder] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const addresses = reserveAddresses
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter((value) => value !== "");

  const incomplete =
    accountId === "" ||
    priceBp === "" ||
    (format === "reserve" && (reserveAmount === "" || addresses.length === 0));

  return (
    <MutationForm
      title="Create a B2B contract"
      description={FORMAT_HELP[format]}
      changes={[
        { label: "account", before: null, after: accountId || "—" },
        { label: "format", before: null, after: format },
        {
          label: "price_bp",
          before: null,
          after: priceBp === "" ? "—" : `${priceBp} (${bpToSun(Number(priceBp))})`,
        },
        ...(format === "reserve"
          ? [
              { label: "reserve_amount", before: null, after: reserveAmount || "—" },
              { label: "reserve_addresses", before: null, after: String(addresses.length) },
            ]
          : []),
        ...(format === "on_demand_guaranteed" && overflowCap !== ""
          ? [
              { label: "overflow_daily_cap_sun", before: null, after: overflowCap },
              {
                label: "beyond the cap",
                before: null,
                after: offerLadder ? "quote the ladder price" : "refuse",
              },
            ]
          : []),
      ]}
      effect={
        termFrom === ""
          ? "From now. A contract prices only quotes taken while it is live; an order already charged keeps its own price and supply policy."
          : `From ${termFrom.replace("T", " ")} UTC. A contract may start in the past — it is not a price version.`
      }
      submitLabel="Create contract"
      busy={busy}
      disabled={incomplete}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .createContract({
            account_id: accountId,
            format,
            price_bp: Number(priceBp),
            ...(format === "reserve"
              ? { reserve_amount: Number(reserveAmount), reserve_addresses: addresses }
              : {}),
            ...(format === "on_demand_guaranteed" && overflowCap !== ""
              ? {
                  overflow_daily_cap_sun: Number(overflowCap),
                  offer_ladder_beyond_cap: offerLadder,
                }
              : {}),
            ...(termFrom === "" ? {} : { term_from: fromUtcInput(termFrom) ?? "" }),
            ...(termTo === "" ? {} : { term_to: fromUtcInput(termTo) ?? "" }),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field label="Account id" error={fieldError(error, "account_id")}>
        <input
          class={inputClass}
          name="account_id"
          value={accountId}
          onInput={(event) => setAccountId((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field label="Format" error={fieldError(error, "format")}>
        <select
          class={inputClass}
          name="format"
          value={format}
          onChange={(event) =>
            setFormat((event.currentTarget as HTMLSelectElement).value as ContractFormat)
          }
        >
          <option value="reserve">reserve</option>
          <option value="on_demand">on_demand</option>
          <option value="on_demand_guaranteed">on_demand_guaranteed</option>
        </select>
      </Field>
      <Field
        label="Contract price (bp of one SUN)"
        hint="Per unit for the on-demand formats, per energy-day for a reserve. 185,000 is 18.50 SUN. NOT clamped to the floor — it is negotiated."
        error={fieldError(error, "price_bp")}
      >
        <input
          class={inputClass}
          name="price_bp"
          type="number"
          min={1}
          value={priceBp}
          onInput={(event) => setPriceBp((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>

      {format === "reserve" ? (
        <>
          <Field
            label="Reserve amount"
            hint="Energy pinned for this customer, every day of the term."
            error={fieldError(error, "reserve_amount")}
          >
            <input
              class={inputClass}
              name="reserve_amount"
              type="number"
              min={1}
              value={reserveAmount}
              onInput={(event) => setReserveAmount((event.currentTarget as HTMLInputElement).value)}
            />
          </Field>
          <Field
            label="Reserve addresses"
            hint="One per line or comma-separated. 1–100 addresses."
            error={fieldError(error, "reserve_addresses")}
          >
            <textarea
              class={`${inputClass} min-h-16 py-1`}
              name="reserve_addresses"
              value={reserveAddresses}
              onInput={(event) =>
                setReserveAddresses((event.currentTarget as HTMLTextAreaElement).value)
              }
            />
          </Field>
        </>
      ) : null}

      {format === "on_demand_guaranteed" ? (
        <>
          <Field
            label="Overflow daily cap (SUN, optional)"
            hint="What we will absorb buying from providers for this customer in one UTC day."
            error={fieldError(error, "overflow_daily_cap_sun")}
          >
            <input
              class={inputClass}
              name="overflow_daily_cap_sun"
              type="number"
              min={0}
              value={overflowCap}
              onInput={(event) => setOverflowCap((event.currentTarget as HTMLInputElement).value)}
            />
          </Field>
          <Field
            label="Beyond the cap"
            hint="Ticked: quote the remainder at ladder prices. Unticked: refuse. Only meaningful with a cap, so it is unavailable until one is set."
            error={fieldError(error, "offer_ladder_beyond_cap")}
          >
            <input
              class="mt-1 size-4 align-middle"
              type="checkbox"
              name="offer_ladder_beyond_cap"
              checked={offerLadder}
              disabled={overflowCap === ""}
              onChange={(event) => setOfferLadder((event.currentTarget as HTMLInputElement).checked)}
            />
          </Field>
        </>
      ) : null}

      <Field label="Term from (UTC, optional)" error={fieldError(error, "term_from")}>
        <input
          class={inputClass}
          name="term_from"
          type="datetime-local"
          value={termFrom}
          onInput={(event) => setTermFrom((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Term to (UTC, optional)" error={fieldError(error, "term_to")}>
        <input
          class={inputClass}
          name="term_to"
          type="datetime-local"
          value={termTo}
          onInput={(event) => setTermTo((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}

export function CloseContractForm({
  contract,
  onDone,
  onCancel,
}: {
  contract: B2bContract;
  onDone(): void;
  onCancel(): void;
}) {
  const [status, setStatus] = useState<"closed" | "suspended">("closed");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <MutationForm
      title={`Close contract ${contract.id}`}
      description="Orders already charged keep the price and the supply policy frozen on their own row, so closing this cannot change how an order the customer has paid for is filled."
      changes={[{ label: "status", before: contract.status, after: status }]}
      effect="At once. Quotes taken after this moment are priced at the public grid."
      submitLabel={status === "closed" ? "Close contract" : "Suspend contract"}
      destructive
      busy={busy}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .closeContract(contract.id, { status, reason })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field label="New status" error={fieldError(error, "status")}>
        <select
          class={inputClass}
          name="status"
          value={status}
          onChange={(event) =>
            setStatus((event.currentTarget as HTMLSelectElement).value as "closed" | "suspended")
          }
        >
          <option value="closed">closed</option>
          <option value="suspended">suspended</option>
        </select>
      </Field>
      <div class="text-[11px] text-ink-muted">
        {contract.format} at {bpToSun(contract.price_bp)} for account {contract.account_id}.
      </div>
    </MutationForm>
  );
}
